"use client";

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { API_BASE, apiRequest, apiRequestMultipart, isLoggedIn, login as apiLogin, logout as apiLogout } from "./api";

export interface MeResponse {
  username: string;
  is_staff: boolean;
  is_admin: boolean;
  is_committee: boolean;
  committee_role: string | null;
  committee_district: string | null;
  owner: Record<string, unknown> | null;
}

interface AuthShape {
  loading: boolean;
  loggedIn: boolean;
  me: MeResponse | null;
  backendConnected: boolean;
  login: (username: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  register: (payload: Record<string, string>, cnicDocument?: File | null) => Promise<{ ok: boolean; error?: string }>;
  logout: () => void;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthShape | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<MeResponse | null>(null);

  const refreshMe = useCallback(async () => {
    if (!API_BASE || !isLoggedIn()) {
      setMe(null);
      return;
    }
    const res = await apiRequest<MeResponse>("/auth/me/");
    setMe(res);
  }, []);

  useEffect(() => {
    refreshMe().finally(() => setLoading(false));
  }, [refreshMe]);

  const value: AuthShape = {
    loading,
    loggedIn: !!me,
    me,
    backendConnected: !!API_BASE,
    login: async (username, password) => {
      const ok = await apiLogin(username, password);
      if (!ok) return { ok: false, error: "Incorrect username or password." };
      await refreshMe();
      return { ok: true };
    },
    register: async (payload, cnicDocument) => {
      let errorMsg = "Registration failed. Please check the form and try again.";
      if (cnicDocument) {
        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => fd.append(k, v));
        fd.append("cnic_document", cnicDocument);
        const res = await apiRequestMultipart<{ access: string; refresh: string; owner: unknown }>(
          "/auth/register/",
          "POST",
          fd,
          (status, body) => {
            errorMsg = typeof body === "object" && body ? JSON.stringify(body) : `HTTP ${status}`;
          }
        );
        if (res) {
          if (typeof window !== "undefined") {
            window.localStorage.setItem("uas_access_token", res.access);
            window.localStorage.setItem("uas_refresh_token", res.refresh);
            window.localStorage.setItem("uas_is_demo_session", "false");
          }
          await refreshMe();
          return { ok: true };
        }
        return { ok: false, error: errorMsg };
      }
      const res2 = await apiRequest<{ access: string; refresh: string; owner: unknown }>(
        "/auth/register/",
        { method: "POST", body: JSON.stringify(payload) },
        (status, body) => {
          errorMsg = typeof body === "object" && body ? JSON.stringify(body) : `HTTP ${status}`;
        }
      );
      if (res2) {
        if (typeof window !== "undefined") {
          window.localStorage.setItem("uas_access_token", res2.access);
          window.localStorage.setItem("uas_refresh_token", res2.refresh);
          window.localStorage.setItem("uas_is_demo_session", "false");
        }
        await refreshMe();
        return { ok: true };
      }
      return { ok: false, error: errorMsg };
    },
    logout: () => {
      apiLogout();
      setMe(null);
    },
    refreshMe,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
